import { Loader2, MapPin } from 'lucide-react'
import React, { useState, useEffect, useRef } from 'react'

import { useGooglePlaces, type PlaceSuggestion } from '@/hooks/useGooglePlaces'
import { cn } from '@/lib/utils'
import { logger } from '@/utils/logger'

import { Card } from './card'
import { Input } from './input'

interface PlacesAutocompleteInputProps {
  value: string
  onChange: (value: string) => void
  onSelect: (placeId: string) => void
  placeholder?: string
  searchOptions?: { country?: string; types?: string[] }
  className?: string
  error?: boolean
  disabled?: boolean
}

export const PlacesAutocompleteInput: React.FC<
  PlacesAutocompleteInputProps
> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Enter address',
  searchOptions,
  className,
  error = false,
  disabled = false,
}) => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)
  const [hasSelectedAddress, setHasSelectedAddress] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionRefs = useRef<(HTMLDivElement | null)[]>([])
  const justSelectedRef = useRef(false)
  const lastSelectedValue = useRef<string>('')

  const { getPlaceSuggestions, isLoaded } = useGooglePlaces()

  // Debounce the search
  useEffect(() => {
    // Don't search if we just selected this value
    if (hasSelectedAddress && value === lastSelectedValue.current) {
      return
    }

    // If value changed significantly from selected value, reset the flag
    if (hasSelectedAddress && value !== lastSelectedValue.current) {
      setHasSelectedAddress(false)
    }

    if (!value || value.length < 3 || !isLoaded || justSelectedRef.current) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true)
      try {
        const results = await getPlaceSuggestions(value, searchOptions)
        setSuggestions(results)
        setShowSuggestions(
          results.length > 0 && !justSelectedRef.current && !hasSelectedAddress
        )
        setActiveSuggestionIndex(-1)
      } catch (error) {
        logger.error('Error fetching suggestions', error)
        setSuggestions([])
        setShowSuggestions(false)
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [value, searchOptions, getPlaceSuggestions, isLoaded, hasSelectedAddress])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleSuggestionClick = (suggestion: PlaceSuggestion) => {
    justSelectedRef.current = true
    setHasSelectedAddress(true)
    lastSelectedValue.current = suggestion.description
    onChange(suggestion.description)
    onSelect(suggestion.placeId)
    setSuggestions([])
    setShowSuggestions(false)
    setActiveSuggestionIndex(-1)
    inputRef.current?.blur()

    // Reset the flag after a longer delay to prevent immediate re-showing
    setTimeout(() => {
      justSelectedRef.current = false
    }, 1000)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveSuggestionIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (
          activeSuggestionIndex >= 0 &&
          activeSuggestionIndex < suggestions.length
        ) {
          handleSuggestionClick(suggestions[activeSuggestionIndex])
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setActiveSuggestionIndex(-1)
        inputRef.current?.blur()
        break
    }
  }

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicks on them
    setTimeout(() => {
      setShowSuggestions(false)
      setActiveSuggestionIndex(-1)
    }, 150)
  }

  const handleInputFocus = () => {
    // Only show suggestions if user hasn't just selected an address
    // and the value has changed from what was selected
    if (
      suggestions.length > 0 &&
      value.length >= 3 &&
      !justSelectedRef.current &&
      (!hasSelectedAddress || value !== lastSelectedValue.current)
    ) {
      setShowSuggestions(true)
    }
  }

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full',
          error && 'border-red-500 dark:border-red-400',
          className
        )}
      />

      {/* Suggestions dropdown */}
      {(loading || (showSuggestions && suggestions.length > 0)) && (
        <Card className="absolute top-full left-0 right-0 z-50 mt-1 p-0 shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center p-3 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Searching addresses...
            </div>
          )}

          {!loading &&
            suggestions.map((suggestion, index) => {
              const isActive = index === activeSuggestionIndex
              const suggestionClassName = cn(
                'flex items-center p-3 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors',
                isActive
                  ? 'bg-blue-50 dark:bg-blue-900'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800'
              )

              return (
                <div
                  key={suggestion.placeId}
                  ref={(el) => {
                    suggestionRefs.current[index] = el
                  }}
                  className={suggestionClassName}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <MapPin className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      {suggestion.mainText}
                    </div>
                    {suggestion.secondaryText && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {suggestion.secondaryText}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
        </Card>
      )}
    </div>
  )
}
